/**
 * Yapılandırma kayıtlarının DURUM ÇIKARIMI — saf fonksiyonlar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * BU DOSYANIN ÜRÜN DEĞERİ: ESKİ SİSTEMİN SESSİZ SAHALARI.
 *
 * 12 Ağustos analizinde 29 saha 7 gündür hiç veri vermiyordu ve kimse fark
 * etmemişti — çünkü hiçbir ekran "bu sahanın panosu yok" ya da "panosu var
 * ama yoklaması kapalı" demiyordu. Liste, kayıt VARSA yeşil görünüyordu.
 *
 * Yapılandırma listeleri telemetri göstermez (o Görev 4), ama YAPILANDIRMA
 * DÜZEYİNDE sessizliğe mahkûm olmuş kaydı gösterebilir: panosuz saha ve
 * yoklaması kapalı pano hiç veri üretmez, bu bir tahmin değil bir kesinliktir.
 *
 * Fonksiyon olarak ayrı duruyorlar ki kuralın kendisi ölçülebilsin — JSX
 * içine gömülü bir üçlü koşul test edilmek yerine gözle kontrol edilirdi.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import type { Tone } from '../design/Status.tsx';

export interface Durum {
  readonly tone: Tone;
  readonly metin: string;
}

/**
 * Sahanın veri üretme YETENEĞİ — telemetrisi değil.
 *
 * "İzleniyor" burada "veri geliyor" demek DEĞİLDİR; "gelmesi bekleniyor"
 * demektir. Sahanın gerçekten okunup okunmadığı `panel_latest.last_ok_at` ile
 * Görev 4'te gösterilecek. İkisini karıştırmamak için metinler ayrı seçildi.
 */
export function sahaDurumu(saha: {
  readonly panel_count: number;
  readonly polling_count: number;
}): Durum {
  // Panosuz saha hiç okunmaz. Alarm değil — eksik yapılandırma [K-015 ayrımı].
  if (saha.panel_count === 0) return { tone: 'offline', metin: 'Pano yok' };
  /*
   * Panosu var ama hepsinin yoklaması kapalı: sistem bu sahadan HİÇ veri
   * beklemiyor. Eski sistemde bu durum görünmezdi ve sessiz sahaların bir
   * kısmı buydu. `warn` çünkü büyük olasılıkla istenmeyen bir durum, ama
   * meşru sebebi de olabilir (bakımdaki saha).
   */
  if (saha.polling_count === 0) return { tone: 'warn', metin: 'İzleme kapalı' };
  if (saha.polling_count < saha.panel_count) {
    return { tone: 'warn', metin: `${saha.polling_count}/${saha.panel_count} pano izleniyor` };
  }
  return { tone: 'ok', metin: saha.panel_count === 1 ? 'İzleniyor' : 'Panoları izleniyor' };
}

/** Panonun yoklama durumu — kapalıysa o panodan veri GELMEZ. */
export function panoDurumu(pano: { readonly is_polling_enabled: boolean }): Durum {
  return pano.is_polling_enabled
    ? { tone: 'ok', metin: 'İzleniyor' }
    : { tone: 'warn', metin: 'İzleme kapalı' };
}

/** Bağlantı türü — [K-005] geçişi pano pano yapılıyor, listede görünmeli. */
export function baglantiEtiketi(mod: 'public' | 'vpn'): string {
  return mod === 'vpn' ? 'VPN' : 'Genel ağ';
}

/**
 * Panonun ULAŞILAN adresi — moda göre DEĞİŞİR.
 *
 * `public_host` ve `tunnel_ip` ikisi birden dolu olabilir (geçiş sırasında);
 * hangisinin kullanıldığını `connection_mode` söyler. Listede yanlışını
 * göstermek, "bu panoya niye bağlanamıyoruz" sorusunu saatlerce yanlış yere
 * baktırırdı — toplayıcı da tam olarak bu seçimi yapıyor (`panel-registry`).
 */
export function erisimAdresi(pano: {
  readonly connection_mode: 'public' | 'vpn';
  readonly public_host: string | null;
  readonly tunnel_ip: string | null;
}): string | null {
  return pano.connection_mode === 'vpn' ? pano.tunnel_ip : pano.public_host;
}

/** Yoklama aralığı — saniye ham hâliyle okunaksız ("300 sn" ≠ "5 dk"). */
export function aralikMetni(saniye: number): string {
  if (saniye % 3600 === 0) return `${saniye / 3600} saat`;
  if (saniye % 60 === 0) return `${saniye / 60} dk`;
  return `${saniye} sn`;
}

/** Tank yerleşimi — sahada mı depoda mı. */
export function tankYeri(tank: {
  readonly site_id: number | null;
  readonly site_name: string | null;
  readonly tank_number: number | null;
}): Durum {
  if (tank.site_id === null) return { tone: 'neutral', metin: 'Depoda' };
  return {
    tone: 'ok',
    metin:
      tank.tank_number === null
        ? (tank.site_name ?? 'Sahada')
        : `${tank.site_name ?? 'Saha'} · Tank ${tank.tank_number}`,
  };
}

export function yonMetni(yon: 'vertical' | 'horizontal' | null): string | null {
  if (yon === null) return null;
  return yon === 'vertical' ? 'Dikey' : 'Yatay';
}
